'use client';
import { OnbForm } from '@/components/form';
import { useStepData } from '@/components/stepData';
import { connectedField } from '@/redux/fields';
import { Grid, Stack, Typography } from '@mui/material';
import { productCodesConst, reviewAndSubmit, salesChannelsConst } from '@repo/onb-api';
import { Checkbox, COLORS, RADIUS } from '@repo/ui';
import { formatter } from '@repo/utils';
import { useId, useRef, useState } from 'react';
import StepWrapper from './stepWrapper';

export default function ReviewAndSubmitCommerce({
  stepname: stepName,
  stepdata: stepDataProp,
}: {
  stepname: reviewAndSubmit['name'];
  stepdata?: reviewAndSubmit['variables'];
}): React.JSX.Element {
  const stepData = useStepData(stepDataProp);
  const {
    // termsAndConditions,
    tradeName,
    legalName,
    taxpayerId,
    firstName,
    lastName,
    mobilePhoneNumber,
    email,
    fiscalAddress,
    salesChannel,
    productCodes,
  } = stepData || {};

  const [termsState, setTermsState] = useState(false);
  const termsRef = useRef<HTMLButtonElement | null>(null);
  const termsTextId = useId();
  const termsErrorId = useId();
  const termsError = termsRef.current?.className?.includes('checkboxError') ?? false;

  return (
    <StepWrapper>
      <OnbForm<reviewAndSubmit> stepName={stepName!} submitLabel="Confirmar" submitBlacklist={['termsAndConditions']}>
        <Stack
          sx={{
            gap: 1,
          }}
        >
          <Typography variant="h1">Estamos a un paso de dar de alta tu comercio</Typography>
          <Typography component="p" variant="subtitle2">
            Revisá que tus datos sean los correctos
          </Typography>
        </Stack>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: COLORS.background[0],
            borderRadius: `${RADIUS.xl}px`,
            gap: 2,
            p: 2,
          }}
        >
          <Stack>
            <Typography variant="body3">Comercio</Typography>
            <Typography component="p" variant="h2">
              {tradeName}
            </Typography>
            <Typography variant="subtitle2">{legalName}</Typography>
            <Typography variant="subtitle2">{formatter(taxpayerId, 'cuit')}</Typography>
          </Stack>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: COLORS.background[0],
            borderRadius: `${RADIUS.xl}px`,
            gap: 2,
            p: 2,
          }}
        >
          <Stack>
            <Typography variant="body3">Representante legal</Typography>
            <Typography component="p" variant="h2">{`${firstName} ${lastName}`}</Typography>
            <Typography variant="subtitle2">{email}</Typography>
            <Typography variant="subtitle2">{formatter(mobilePhoneNumber, 'phone')}</Typography>
          </Stack>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: COLORS.background[0],
            borderRadius: `${RADIUS.xl}px`,
            gap: 2,
            p: 2,
          }}
        >
          <Stack>
            <Typography variant="body3">Dirección</Typography>
            <Typography component="p" variant="h2">
              {fiscalAddress}
            </Typography>
          </Stack>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: COLORS.background[0],
            borderRadius: `${RADIUS.xl}px`,
            gap: 2,
            p: 2,
          }}
        >
          {salesChannel && (
            <Stack>
              <Typography variant="body3">Tipo de tienda</Typography>
              <Typography component="p" variant="h2">
                {salesChannelsConst[salesChannel]}
              </Typography>
            </Stack>
          )}
        </Grid>
        {productCodes && (
          <Grid
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: COLORS.background[0],
              borderRadius: `${RADIUS.xl}px`,
              gap: 2,
              p: 2,
            }}
          >
            <Stack>
              <Typography variant="body3">Producto</Typography>
              <Typography component="p" variant="h2">
                {productCodes.map((code) => (code ? productCodesConst[code] : '')).join(', ')}
              </Typography>
            </Stack>
          </Grid>
        )}
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '8px !important',
            px: 1,
            borderColor: !termsState && termsError ? COLORS.state.error : 'rgba(0, 0, 0, 0.23)',
          }}
        >
          <Checkbox
            ref={(el) => {
              termsRef.current = el;
            }}
            slotProps={{
              input: {
                'aria-labelledby': termsTextId,
                'aria-describedby': !termsState && termsError ? termsErrorId : undefined,
              },
            }}
            border="none"
            {...connectedField({
              checkbox: true,
              stepName,
              name: 'hasTermsAccepted',
              required: true,
              beforeChange: (value) => {
                setTermsState(!!value);
                return !!value;
              },
              errorHandler: {
                type: 'checkbox',
              },
            })}
          />
          <Typography
            id={termsTextId}
            variant="body2"
            color="primary"
            sx={{
              py: 1,
            }}
          >
            Declaro que he leído y acepto los
            <br />
            <a
              href="https://cdn.pro.directo.com.ar/assets/documents/Solicitud+de+incorporacio%CC%81n+como+comercializador+de+servicios+de+DIRECTO+SA.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 'bold' }}
            >
              Términos y Condiciones
            </a>
          </Typography>
        </Stack>
        {!termsState && termsError && (
          <Typography id={termsErrorId} role="alert" variant="body2" sx={{ textAlign: 'center' }} color="error">
            Marcá la casilla para continuar
          </Typography>
        )}
      </OnbForm>
    </StepWrapper>
  );
}
